/**
 * The danger model: a cheap per-scan picture of where Digby should not go, for the path search.
 * It is advice, not proof - every move is still checked by look-ahead (`safety.ts`) - so it only
 * has to steer routes away from trouble, not be exact.
 */

import { E } from '../sim';
import type { CaveState } from '../sim';

// Local copies: the hot loops below must not pay for a module-binding lookup per cell.
const { SPACE, DIRT, STEEL, DIGBY, BOULDER, DIAMOND, BOULDER_FALLING, DIAMOND_FALLING, FIREFLY, BUTTERFLY } = E;

/** `block` values: a falling object's path clears soon; a creature's neighbourhood never is safe. */
const BLOCK_FALL = 1;
const BLOCK_CREATURE = 2;

/** Extra step cost near a creature's next few positions (index: how many moves away it is). */
const CREATURE_COST: readonly number[] = [0, 40, 20, 10];
const REACH = CREATURE_COST.length - 1;
/** Digging into the space a creature roams lets it out: only when there is no other way. */
const BREACH = 30;
/** The most space cells followed from one creature. */
const ROAM = 200;
const BREACHED = 32000;
/** Entering the cell under a boulder frees it once Digby leaves: fine, but not for nothing. */
const UNDER_BOULDER = 3;
const UNDER_DIAMOND = 1;
/** A cell whose planned step was refused lately: find another way if there is one. */
const AVOIDED = 25;
const MAX_EXTRA = 60000;

/** A firefly or a butterfly, any heading. */
export function isCreature(el: number): boolean {
  return (el >= FIREFLY && el < FIREFLY + 4) || (el >= BUTTERFLY && el < BUTTERFLY + 4);
}

export class DangerMap {
  /** Non-zero: do not route through (`BLOCK_FALL` or `BLOCK_CREATURE`). */
  readonly block: Uint8Array;
  /** Extra route cost per cell. */
  readonly extra: Uint16Array;
  private readonly depth: Int16Array;
  private readonly queue: Int32Array;

  constructor(readonly n: number) {
    this.block = new Uint8Array(n);
    this.extra = new Uint16Array(n);
    this.depth = new Int16Array(n).fill(-1);
    this.queue = new Int32Array(n);
  }

  /** `avoid`: the scan until which each cell is to be routed around. */
  build(s: CaveState, avoid: Int32Array): void {
    this.block.fill(0);
    this.extra.fill(0);
    const { w, cells, scan } = s;
    const n = cells.length;
    for (let i = 0; i < n; i++) {
      if ((avoid[i] ?? 0) > scan) this.bump(i, AVOIDED);
      const el = cells[i] ?? STEEL;
      if (el < BOULDER) continue;
      if (isCreature(el)) {
        this.creature(s, i);
        continue;
      }
      if (el !== BOULDER && el !== DIAMOND && el !== BOULDER_FALLING && el !== DIAMOND_FALLING) continue;
      const below = i + w < n ? (cells[i + w] ?? STEEL) : STEEL;
      if (el === BOULDER_FALLING || el === DIAMOND_FALLING || below === SPACE) this.fallPath(s, i);
      else if (i + w < n) this.bump(i + w, el === BOULDER ? UNDER_BOULDER : UNDER_DIAMOND);
    }
  }

  private bump(i: number, cost: number): void {
    const v = (this.extra[i] ?? 0) + cost;
    this.extra[i] = v > MAX_EXTRA ? MAX_EXTRA : v;
  }

  /** The space cells a falling object will pass through, down to where it lands. */
  private fallPath(s: CaveState, i: number): void {
    const { w, cells } = s;
    for (let j = i + w; j < cells.length; j += w) {
      const el = cells[j] ?? STEEL;
      if (el !== SPACE && el !== DIGBY) break;
      if ((this.block[j] ?? 0) < BLOCK_FALL) this.block[j] = BLOCK_FALL;
    }
  }

  /**
   * Block the creature's neighbours; make the neighbours of where it can get to soon costly, and
   * the dirt walling in the space it roams.
   */
  private creature(s: CaveState, start: number): void {
    const { w, h, cells } = s;
    const depth = this.depth;
    const queue = this.queue;
    let head = 0;
    let tail = 0;
    queue[tail++] = start;
    depth[start] = 0;
    while (head < tail && tail < ROAM) {
      const c = queue[head++] ?? 0;
      const d = depth[c] ?? 0;
      const x = c % w;
      const y = (c - x) / w;
      for (let k = 0; k < 4; k++) {
        const nx = x + (k === 1 ? 1 : k === 3 ? -1 : 0);
        const ny = y + (k === 0 ? -1 : k === 2 ? 1 : 0);
        if (nx < 0 || ny < 0 || nx >= w || ny >= h) continue;
        const nb = ny * w + nx;
        if (d === 0) this.block[nb] = BLOCK_CREATURE;
        else if (d <= REACH) this.bump(nb, CREATURE_COST[d] ?? 0);
        if ((depth[nb] ?? 0) >= 0) continue;
        const el = cells[nb];
        // Creatures only ever move into space.
        if (el === SPACE) {
          depth[nb] = d + 1;
          queue[tail++] = nb;
        } else if (el === DIRT) {
          depth[nb] = BREACHED;
          queue[tail++] = nb;
          this.bump(nb, BREACH);
        }
      }
      // Dirt cells sit in the queue only to be reset below; they spread nothing.
      while (head < tail && depth[queue[head] ?? 0] === BREACHED) head++;
    }
    for (let q = 0; q < tail; q++) depth[queue[q] ?? 0] = -1;
    this.block[start] = BLOCK_CREATURE;
  }
}
